import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaChevronDown } from 'react-icons/fa';
import logo from '@/assets/Header/magnetix.png';
import './Header.css';

interface NavLinkProps {
  to: string;
  children: React.ReactNode;
  onClick?: () => void;
}

const languages = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
];

const Header: React.FC = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedLanguage] = useState(languages[0]);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [comingSoonLang, setComingSoonLang] = useState<string | null>(null);
  const langDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 800);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target as Node)) {
        setIsLangDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => setIsOpen(false);

  const handleNavClick = () => {
    closeMenu();
  };

  const handleLanguageSelect = (language: typeof languages[0]) => {
    if (language.code === selectedLanguage.code) {
      setIsLangDropdownOpen(false);
      return;
    }
    setComingSoonLang(language.code);
    window.setTimeout(() => setComingSoonLang(null), 2000);
  };

  const NavLink: React.FC<NavLinkProps> = ({ to, children, onClick }) => (
    <Link
      to={to}
      className="header-nav-link"
      onClick={onClick}
    >
      {children}
    </Link>
  );

  const LanguageSelector = () => (
    <div className="header-language-selector" ref={langDropdownRef}>
      <button
        className="header-language-button"
        onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
        aria-label="Select language"
      >
        <span className="header-language-text">{selectedLanguage.code.toUpperCase()}</span>
        <FaChevronDown className={`header-language-chevron ${isLangDropdownOpen ? 'open' : ''}`} />
      </button>

      {isLangDropdownOpen && (
        <div className="header-language-dropdown">
          {languages.map((lang) => (
            <div key={lang.code} className="header-language-option-wrapper">
              <button
                className={`header-language-option ${selectedLanguage.code === lang.code ? 'active' : ''}`}
                onClick={() => handleLanguageSelect(lang)}
              >
                <span className="header-language-flag">{lang.flag}</span>
                <span className="header-language-name">{lang.name}</span>
              </button>
              {comingSoonLang === lang.code && (
                <span className="header-language-coming-soon">Available soon</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="header-wrapper">
        <div className="header-container">
          {/* Mobile Layout: Hamburger + Logo + ThemeToggle */}
          {isMobile ? (
            <>
              {/* Left Side: Hamburger Menu */}
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '16px' }}>
              <button
                className="header-hamburger"
                onClick={toggleMenu}
                aria-label="Toggle menu"
                aria-expanded={isOpen}
              >
                {isOpen ? (
                  // Cross Icon
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                ) : (
                  // Hamburger Icon
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" strokeWidth="2">
                    <path d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>

              {/* Left Side: Logo and Text */}
              <div className="header-logo-wrapper">
                <Link to="/" className="header-logo-section">
                  <img
                    src={logo}
                    alt="Magnetix Logo"
                    className="header-logo-image"
                  />
                  <span className="header-logo-text">Magnetix</span>
                </Link>
              </div>
              </div>
              {/* Right Side: Language Selector + Theme Toggle */}
              <div className="header-mobile-right-group">
                <LanguageSelector />
              </div>
            </>
          ) : (
            <>
              {/* Desktop Layout: Logo + Nav + Buttons */}
              <div className="header-logo-wrapper">
                <Link to="/" className="header-logo-section">
                  <img
                    src={logo}
                    alt="Magnetix Logo"
                    className="header-logo-image"
                  />
                  <span className="header-logo-text">Magnetix</span>
                </Link>
              </div>

              {/* Desktop Navigation */}
              <nav className="header-nav">
                <NavLink to="/">Home</NavLink>
                {/* <NavLink to="/features">Features</NavLink> */}
                {/* <NavLink to="/pricing">Pricing</NavLink> */}
                <NavLink to="/about">About</NavLink>
                <NavLink to="/contact">Contact Us</NavLink>
              </nav>

              {/* Desktop Buttons */}
              <div className="header-button-group">
                <button
                  className="header-login-button"
                  onClick={() => navigate('/login')}
                >
                  Login
                </button>
                <LanguageSelector />
              </div>
            </>
          )}
        </div>
   

      {/* Mobile Sidebar */}
      {isOpen && (
        <div className="header-overlay" onClick={closeMenu}>
          <div className="header-sidebar" onClick={(e) => e.stopPropagation()}>
            <nav className="header-mobile-nav">
              <NavLink to="/" onClick={handleNavClick}>Home</NavLink>
              {/* <NavLink to="/features" onClick={handleNavClick}>Features</NavLink> */}
              {/* <NavLink to="/pricing" onClick={handleNavClick}>Pricing</NavLink> */}
              <NavLink to="/about" onClick={handleNavClick}>About</NavLink>
              <NavLink to="/contact" onClick={handleNavClick}>Contact Us</NavLink>
            </nav>

            <div className="header-mobile-buttons">
              <button
                className="header-login-button header-mobile-button"
                onClick={() => {
                  navigate('/login');
                  closeMenu();
                }}
              >
                Login
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;